// src/components/SettingsPanel.tsx
import React, { useState, useEffect } from 'react';
import { TbX } from 'react-icons/tb';
import { ApiKeysModal } from './ApiKeysModal';
import './SettingsPanel.css';

interface TaskCounts {
  total: number;
  high: number;
  medium: number;
  low: number;
}

interface SettingsPanelProps {
  onClose: () => void;
  isModal?: boolean;
  userTimezone: string;
  onTimezoneChange: (timezone: string) => void;
  userId: string;
  onRebucketAll: () => void;
  onRerankBucket: (bucket: 'high' | 'medium' | 'low') => void;
  taskCounts: TaskCounts;
  onLogout: () => void;
  width?: number;
  onWidthChange?: (width: number) => void;
}

const COMMON_TIMEZONES = [
  'Pacific/Honolulu',
  'America/Anchorage',
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Sao_Paulo',
  'Atlantic/Reykjavik',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Moscow',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Bangkok',
  'Asia/Singapore',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Australia/Sydney',
  'Pacific/Auckland',
];

export const SettingsPanel: React.FC<SettingsPanelProps> = ({
  onClose,
  isModal = false,
  userTimezone,
  onTimezoneChange,
  userId,
  onRebucketAll,
  onRerankBucket,
  taskCounts,
  onLogout,
  width = 305,
  onWidthChange,
}) => {
  const [isApiKeysModalOpen, setIsApiKeysModalOpen] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  // Handle resize
  useEffect(() => {
    if (!isResizing || isModal) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = e.clientX - 64;
      if (onWidthChange) {
        onWidthChange(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, isModal, onWidthChange]);

  const allTimezones = COMMON_TIMEZONES.includes(userTimezone)
    ? COMMON_TIMEZONES
    : [userTimezone, ...COMMON_TIMEZONES];

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const panelContent = (
    <div
      className={`settings-panel ${isResizing ? 'resizing' : ''}`}
      style={!isModal ? { width: `${width}px` } : undefined}
    >
      {!isModal && (
        <div
          className="panel-resize-handle"
          onMouseDown={() => setIsResizing(true)}
        />
      )}
      <div className="settings-panel-header">
        <span className="settings-panel-title">Settings</span>
        <button className="settings-panel-close" onClick={onClose} aria-label="Close settings">
          <TbX />
        </button>
      </div>
      <div className="settings-panel-content">
        <div className="settings-section">
          <label className="settings-label" htmlFor="timezone-select">
            Timezone
          </label>
          <select
            id="timezone-select"
            className="timezone-select"
            value={userTimezone}
            onChange={e => onTimezoneChange(e.target.value)}
          >
            {allTimezones.map(tz => (
              <option key={tz} value={tz}>
                {tz.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <p className="settings-description">
            Your timezone is used to determine when "Today" tasks expire at
            midnight.
          </p>
        </div>

        <div className="settings-section">
          <label className="settings-label">API Access</label>
          <button
            className="api-keys-btn"
            onClick={() => setIsApiKeysModalOpen(true)}
          >
            Manage API Keys
          </button>
          <p className="settings-description">
            Generate API keys to access your tasks from external applications.
          </p>
        </div>

        <div className="settings-section">
          <label className="settings-label">Task Priorities</label>
          <button
            className="settings-action-btn full-width"
            onClick={() => {
              onRebucketAll();
              onClose();
            }}
          >
            Re-bucket All Tasks
            <span className="settings-btn-count">{taskCounts.total} tasks</span>
          </button>
          <p className="settings-description">
            Go through each task and assign to High, Medium, or Low priority.
          </p>
        </div>

        <div className="settings-section">
          <label className="settings-label">Tournament Re-rank by Bucket</label>
          <div className="settings-button-stack">
            <button
              className="settings-action-btn full-width high"
              onClick={() => {
                onRerankBucket('high');
                onClose();
              }}
              disabled={taskCounts.high < 2}
            >
              High
              <span className="settings-btn-count">{taskCounts.high} tasks</span>
            </button>
            <button
              className="settings-action-btn full-width medium"
              onClick={() => {
                onRerankBucket('medium');
                onClose();
              }}
              disabled={taskCounts.medium < 2}
            >
              Medium
              <span className="settings-btn-count">{taskCounts.medium} tasks</span>
            </button>
            <button
              className="settings-action-btn full-width low"
              onClick={() => {
                onRerankBucket('low');
                onClose();
              }}
              disabled={taskCounts.low < 2}
            >
              Low
              <span className="settings-btn-count">{taskCounts.low} tasks</span>
            </button>
          </div>
          <p className="settings-description">
            Run a tournament-style comparison within a specific priority bucket.
          </p>
        </div>

        <div className="settings-section settings-logout-section">
          <button className="logout-btn" onClick={onLogout}>
            Log out
          </button>
        </div>
      </div>

      <ApiKeysModal
        isOpen={isApiKeysModalOpen}
        onClose={() => setIsApiKeysModalOpen(false)}
        userId={userId}
      />
    </div>
  );

  if (isModal) {
    return (
      <div className="settings-modal" onClick={handleOverlayClick}>
        {panelContent}
      </div>
    );
  }

  return panelContent;
};
